import getFontDefinition from 'editor/src/store/fonts/selector/getFontDefinition';
import { addUploadedFontsAction } from 'editor/src/store/fonts/slice';
import { FontStatus, UploadedFontData } from 'editor/src/store/fonts/types';
import loadFont from 'editor/src/store/fonts/utils/loadFont';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import { captureException } from 'editor/src/logger';

import updateFontStatusOperation from './updateFontStatusOperation';

export const loadingPromise = new Map<string, Promise<any>>();

const loadFontOperation = (fontFile: string) => async (dispatch: ThunkDispatch, getState: () => RootState) => {
  const state = getState();
  const fontDefinition = getFontDefinition(state, fontFile);
  if (!fontDefinition) {
    captureException(`unknown font: ${fontFile}`);
    // Add a failed placeholder so useFontLoader's fallback (render with default font) can trigger.
    // Without this the font status is never set and the text element renders nothing.
    const placeholder: UploadedFontData = {
      fontFile,
      filename: fontFile,
      name: fontFile,
      family: fontFile,
      weight: 'Regular',
      style: 'Normal',
      loadingStatus: FontStatus.failed,
      isMissing: true,
    };
    dispatch(addUploadedFontsAction([placeholder]));
    return;
  }

  if (fontDefinition.status === FontStatus.ready) {
    return;
  }

  const url = fontDefinition.metadata.fileUrl;

  // Protected custom fonts have no fileUrl — skip loading, mark as failed for fallback
  if (!url && fontDefinition.isUploaded) {
    dispatch(updateFontStatusOperation({ fontFile, fontStatus: FontStatus.failed }));
    return;
  }

  if (fontDefinition.status === FontStatus.loading && loadingPromise.has(url)) {
    try {
      await loadingPromise.get(url);
    } catch {
      // we already catch and log this
    }
    return;
  }

  dispatch(updateFontStatusOperation({ fontFile, fontStatus: FontStatus.loading }));

  try {
    const promise = loadFont(fontDefinition, {
      executor: 'loadFontOperation',
      fontDefinition,
    });
    loadingPromise.set(url, promise);
    await promise;
    dispatch(updateFontStatusOperation({ fontFile, fontStatus: FontStatus.ready }));
  } catch {
    const def = getFontDefinition(getState(), fontFile);
    if (def?.status === FontStatus.loading) {
      dispatch(
        updateFontStatusOperation({
          fontFile,
          fontStatus: FontStatus.failed,
        }),
      );
    }
  } finally {
    loadingPromise.delete(url);
  }
};

export default loadFontOperation;
